import { useEffect, useState, useCallback } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Badge,
  Banner,
  TextField,
  DataTable,
  Spinner,
  Select,
} from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';

interface ProductFlowItem {
  product_id: number;
  sku: string;
  title: string;
  received: number;
  dispatched: number;
  adjusted_in: number;
  adjusted_out: number;
  transferred: number;
  movement_count: number;
}

interface ProductFlowSummary {
  total_received: number;
  total_dispatched: number;
  net_adjustments: number;
  total_movements: number;
  products_moved: number;
}

export default function ProductFlowReport() {
  const navigate = useNavigate();
  const { fetchApi, error, loading } = useApi();
  const [data, setData] = useState<ProductFlowItem[]>([]);
  const [summary, setSummary] = useState<ProductFlowSummary | null>(null);
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });
  const [flowType, setFlowType] = useState('');
  const [exporting, setExporting] = useState(false);

  const flowTypeOptions = [
    { label: 'All Movements', value: '' },
    { label: 'Stock In (Receive + Adj In)', value: 'in' },
    { label: 'Stock Out (Dispatch + Adj Out)', value: 'out' },
    { label: 'Transfers Only', value: 'transfer' },
  ];

  const loadReport = useCallback(async () => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (search) params.append('search', search);
    if (flowType) params.append('flow_type', flowType);

    const result = await fetchApi<{
      data: ProductFlowItem[];
      summary: ProductFlowSummary;
    }>(`/reports/product-flow?${params.toString()}`);

    if (result) {
      setData(result.data || []);
      setSummary(result.summary);
    }
  }, [fetchApi, startDate, endDate, search, flowType]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const result = await fetchApi<{ success: boolean; download_url: string; row_count: number }>('/reports/product-flow/export', {
        method: 'POST',
        body: JSON.stringify({ start_date: startDate, end_date: endDate, flow_type: flowType || undefined }),
      });

      if (result?.success && result.download_url) {
        window.open(`${import.meta.env.VITE_API_URL}${result.download_url}`, '_blank');
      }
    } finally {
      setExporting(false);
    }
  };

  const getFlowBadge = (value: number, type: 'in' | 'out' | 'neutral') => {
    if (value === 0) return <Text as="span" variant="bodySm" tone="subdued">0</Text>;
    const tone = type === 'in' ? 'success' : type === 'out' ? 'critical' : 'info';
    return <Badge tone={tone}>{String(value)}</Badge>;
  };

  const tableRows = data.map((item) => [
    <Button variant="plain" onClick={() => navigate(`/products/${item.product_id}`)}>
      {item.sku || '-'}
    </Button>,
    <Text as="span" variant="bodySm" fontWeight="semibold">{item.title}</Text>,
    getFlowBadge(item.received, 'in'),
    getFlowBadge(item.dispatched, 'out'),
    getFlowBadge(item.adjusted_in, 'in'),
    getFlowBadge(item.adjusted_out, 'out'),
    getFlowBadge(item.transferred, 'neutral'),
    String(item.movement_count),
  ]);

  return (
    <Page
      title="Product Flow Report"
      subtitle="Stock movements by product over time"
      backAction={{ content: 'Reports', onAction: () => navigate('/reports') }}
      primaryAction={{
        content: 'Export CSV',
        onAction: handleExport,
        loading: exporting,
        disabled: data.length === 0,
      }}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical">{error}</Banner>
          </Layout.Section>
        )}

        {/* Summary Cards */}
        {summary && (
          <Layout.Section>
            <InlineStack gap="400" wrap={false}>
              <Card>
                <BlockStack gap="200">
                  <Text as="span" variant="bodySm" tone="subdued">Total Received</Text>
                  <InlineStack gap="200" align="center">
                    <Text as="p" variant="heading2xl">{summary.total_received}</Text>
                    <Badge tone="success">IN</Badge>
                  </InlineStack>
                </BlockStack>
              </Card>
              <Card>
                <BlockStack gap="200">
                  <Text as="span" variant="bodySm" tone="subdued">Total Dispatched</Text>
                  <InlineStack gap="200" align="center">
                    <Text as="p" variant="heading2xl">{summary.total_dispatched}</Text>
                    <Badge tone="critical">OUT</Badge>
                  </InlineStack>
                </BlockStack>
              </Card>
              <Card>
                <BlockStack gap="200">
                  <Text as="span" variant="bodySm" tone="subdued">Net Adjustments</Text>
                  <Text as="p" variant="heading2xl">
                    {summary.net_adjustments >= 0 ? '+' : ''}{summary.net_adjustments}
                  </Text>
                </BlockStack>
              </Card>
              <Card>
                <BlockStack gap="200">
                  <Text as="span" variant="bodySm" tone="subdued">Products Moved</Text>
                  <Text as="p" variant="heading2xl">{summary.products_moved}</Text>
                </BlockStack>
              </Card>
            </InlineStack>
          </Layout.Section>
        )}

        {/* Filters */}
        <Layout.Section>
          <Card>
            <InlineStack gap="400" align="start">
              <div style={{ width: '160px' }}>
                <TextField
                  label="Start Date"
                  type="date"
                  value={startDate}
                  onChange={setStartDate}
                  autoComplete="off"
                  labelHidden
                />
              </div>
              <div style={{ width: '160px' }}>
                <TextField
                  label="End Date"
                  type="date"
                  value={endDate}
                  onChange={setEndDate}
                  autoComplete="off"
                  labelHidden
                />
              </div>
              <div style={{ width: '200px' }}>
                <Select
                  label="Flow Type"
                  labelHidden
                  options={flowTypeOptions}
                  value={flowType}
                  onChange={setFlowType}
                />
              </div>
              <div style={{ width: '250px' }}>
                <TextField
                  label="Search"
                  labelHidden
                  value={search}
                  onChange={setSearch}
                  autoComplete="off"
                  placeholder="Search SKU or product..."
                  clearButton
                  onClearButtonClick={() => setSearch('')}
                />
              </div>
              <Button onClick={loadReport}>Refresh</Button>
            </InlineStack>
          </Card>
        </Layout.Section>

        {/* Data Table */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <Text as="h2" variant="headingMd">
                  Product Movements ({data.length})
                </Text>
                <Text as="span" variant="bodySm" tone="subdued">
                  {startDate} to {endDate}
                </Text>
              </InlineStack>

              {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                  <Spinner />
                </div>
              ) : data.length === 0 ? (
                <Banner tone="info">
                  No stock movements found for the selected date range.
                </Banner>
              ) : (
                <DataTable
                  columnContentTypes={['text', 'text', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric']}
                  headings={['SKU', 'Product', 'Received', 'Dispatched', 'Adj In', 'Adj Out', 'Transferred', 'Movements']}
                  rows={tableRows}
                  sortable={[false, false, true, true, true, true, true, true]}
                />
              )}
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
